import React, { useState, useEffect, useMemo } from 'react';
import { Slide, ShopMembership, MembershipRole } from '../types';
import { slideApiService } from '../services/slideApiService';
import { shopService } from '../services/shopService';
import ConfirmModal from './ConfirmModal';
import {
  DEFAULT_SHARE_DURATION, SHARE_DURATIONS, ShareDurationId, hasPlaceholderImage, isClientMember,
  shareDurationRequest,
} from '../utils/shareOptions';

/**
 * Share a named collection with clients as a PUBLISHED_FOR_YOU feed cube (C5 / C1).
 *
 * Audience (carousel-svc/CLIENT_ROLE_SHARE_AND_DURATION_PROPOSAL.md, U1, 2026-09-26): EITHER all
 * clients, sent as roles: ["CLIENT"] and resolved at read time so clients who join later see it too,
 * OR specific CLIENT members (the list shows client members only; the team gathers slides with
 * shop-visible collections instead). Both together would show a client the same slides, so the UI
 * offers one or the other; the API still accepts both. Default is "Specific clients" so a broadcast
 * to every client is always a deliberate choice.
 * Duration (U2): 4 days (default) to "Until unshared"; see utils/shareOptions.ts.
 *
 * Scope carried over from the 2026-07-10 MVP decisions:
 * - No pre-fill of the current audience/title on re-share (that data lives in the BQ cube with no
 *   read endpoint); each share is specified fresh, and re-share replaces the cube (D10).
 * - P6 = BLOCK + publish manually: if any slide in the collection isn't published, Share is
 *   disabled and the offending slides are listed; the owner publishes them (Slides tab) and
 *   re-opens. No auto-publish.
 * - Placeholder pre-check (proposal S2, 2026-09-26): same BLOCK + list for any slide that still holds
 *   the CSV-import placeholder image (published or not). slide-svc enforces both as the backstop.
 * - Un-share (C1b) available as a secondary action (idempotent).
 */
interface ShareCollectionModalProps {
  shopId: string;
  collectionId: string;
  collectionName: string;
  slides: Slide[]; // the collection's hydrated slides (for the P6 publish pre-check)
  onClose: () => void;
  onShared: (message: string) => void; // parent shows a toast + closes
}

const PRIMARY = 'var(--text-accent)';
const BORDER = 'var(--border-light)';
const MUTED = 'var(--text-accent)';
const TEXT = 'var(--text-accent)';
const WARN_BG = 'var(--warning-bg)';
const WARN_BORDER = 'var(--warning)';
const WARN_TEXT = 'var(--warning-text)';

const TITLE_MAX = 120;
const REASON_MAX = 500;

const roleLabel = (role: MembershipRole): string => {
  const names: Record<MembershipRole, string> = {
    PLATFORM_ADMIN: 'Platform Admin', OWNER: 'Owner', ADMIN: 'Admin',
    MEMBER: 'Member', VIEWER: 'Viewer', CLIENT: 'Client', VENDOR: 'Vendor',
  };
  return names[role] || role;
};

/** A share pre-check that failed: what's wrong, how to fix it, and the offending slides by name. */
const SlideGateWarning: React.FC<{ heading: string; slides: Slide[]; children: React.ReactNode }> = ({ heading, slides, children }) => (
  <div style={{ backgroundColor: WARN_BG, border: `1px solid ${WARN_BORDER}`, borderRadius: '8px', padding: '12px 14px', marginBottom: '16px' }}>
    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: WARN_TEXT, marginBottom: '6px' }}>{heading}</div>
    <div style={{ fontSize: '0.8125rem', color: WARN_TEXT, marginBottom: '8px' }}>{children}</div>
    {/* Bounded, internally-scrolling list so a long set of slides doesn't push the Recipients
        picker and Share button off-screen. */}
    <ul style={{ margin: 0, paddingLeft: '18px', paddingRight: '4px', fontSize: '0.8125rem', color: WARN_TEXT, maxHeight: '220px', overflowY: 'auto' }}>
      {slides.map(s => (
        <li key={s.id} style={{ marginBottom: '2px' }}>{s.name || s.id}</li>
      ))}
    </ul>
  </div>
);

const ShareCollectionModal: React.FC<ShareCollectionModalProps> = ({
  shopId, collectionId, collectionName, slides, onClose, onShared,
}) => {
  const [members, setMembers] = useState<ShopMembership[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [membersError, setMembersError] = useState<string | null>(null);

  const [audience, setAudience] = useState<'all' | 'specific'>('specific');
  const [duration, setDuration] = useState<ShareDurationId>(DEFAULT_SHARE_DURATION);
  const [selectedUids, setSelectedUids] = useState<Set<string>>(new Set());
  const [title, setTitle] = useState('');
  const [reason, setReason] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmUnshare, setConfirmUnshare] = useState(false);

  // P6 pre-check: a slide can only be shared if it's published (which the server guarantees implies
  // >=1 published image). Missing `published` => unpublished.
  const unpublishedSlides = useMemo(
    () => slides.filter(s => s.published !== true),
    [slides]
  );
  const placeholderSlides = useMemo(() => slides.filter(hasPlaceholderImage), [slides]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingMembers(true);
        setMembersError(null);
        const all = await shopService.getShopMemberships(shopId);
        // Only ACTIVE CLIENT members: shares are for clients, and the feed filters on active membership.
        if (!cancelled) setMembers(all.filter(m => m.active && isClientMember(m)));
      } catch (err) {
        console.error('Failed to load shop members:', err);
        if (!cancelled) setMembersError('Failed to load shop members.');
      } finally {
        if (!cancelled) setLoadingMembers(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shopId]);

  const toggleMember = (uid: string) => {
    setSelectedUids(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const memberName = (m: ShopMembership) =>
    m.displayName || m.email || m.firebaseUid;

  const canShare =
    !submitting &&
    unpublishedSlides.length === 0 &&
    placeholderSlides.length === 0 &&
    (audience === 'all' || selectedUids.size > 0) &&
    title.trim().length > 0;

  const durationLabel = (SHARE_DURATIONS.find(d => d.id === duration)?.label ?? '').toLowerCase();

  const handleShare = async () => {
    if (!canShare) return;
    try {
      setSubmitting(true);
      setError(null);
      await slideApiService.shareCollection(shopId, collectionId, {
        ...(audience === 'all'
          ? { roles: ['CLIENT'], userUids: [] }
          : { userUids: Array.from(selectedUids) }),
        title: title.trim(),
        reason: reason.trim() || undefined,
        ...shareDurationRequest(duration),
      });
      const who = audience === 'all'
        ? 'all clients'
        : `${selectedUids.size} ${selectedUids.size === 1 ? 'client' : 'clients'}`;
      const until = duration === 'forever' ? 'until you stop sharing' : `for ${durationLabel}`;
      onShared(`Shared "${collectionName}" with ${who} ${until}. It will appear in their feed shortly.`);
    } catch (err) {
      console.error('Failed to share collection:', err);
      setError(parseError(err));
      setSubmitting(false);
    }
  };

  // Runs after the CSAI-styled confirmation (ConfirmModal). On failure the confirmation closes and
  // the detailed error shows in this dialog's existing error area, as before.
  const handleUnshare = async () => {
    if (submitting) return;
    try {
      setSubmitting(true);
      setError(null);
      await slideApiService.unshareCollection(shopId, collectionId);
      onShared(`Stopped sharing "${collectionName}".`);
    } catch (err) {
      console.error('Failed to un-share collection:', err);
      setConfirmUnshare(false);
      setError(parseError(err));
      setSubmitting(false);
    }
  };

  return (
    <>
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: 'var(--bg-card)', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '520px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
      >
        <h3 style={{ margin: '0 0 4px', fontSize: '1.125rem', fontWeight: 600, color: TEXT }}>
          Share "{collectionName}"
        </h3>
        <p style={{ margin: '0 0 16px', fontSize: '0.875rem', color: MUTED }}>
          Send this collection to clients as a curated feed cube. They see only published photos and the listing price, never your cost, location, or internal details.
        </p>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {/* P6 publish gate */}
          {unpublishedSlides.length > 0 && (
            <SlideGateWarning
              heading={`${unpublishedSlides.length} ${unpublishedSlides.length === 1 ? 'slide isn’t' : 'slides aren’t'} published yet`}
              slides={unpublishedSlides}
            >
              Every shared slide needs to be published (with at least one published photo) first. Open these in the Slides tab, turn on <strong>Published</strong>, then re-open Share:
            </SlideGateWarning>
          )}

          {/* Placeholder gate (S2) */}
          {placeholderSlides.length > 0 && (
            <SlideGateWarning
              heading={`${placeholderSlides.length} ${placeholderSlides.length === 1 ? 'slide still has' : 'slides still have'} a placeholder photo`}
              slides={placeholderSlides}
            >
              Clients can’t see placeholder photos. Open these in the Slides tab, add a real photo and delete the placeholder, then re-open Share:
            </SlideGateWarning>
          )}

          {/* Recipients: all clients (incl. future) OR specific clients */}
          <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: TEXT, marginBottom: '8px' }}>Share with</div>
          <div role="radiogroup" aria-label="Share with" style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
            {([['specific', 'Specific clients'], ['all', 'All clients']] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={audience === value}
                className={`btn btn-sm ${audience === value ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setAudience(value)}
              >
                {label}
              </button>
            ))}
          </div>
          {audience === 'all' ? (
            <div style={{ fontSize: '0.8125rem', color: MUTED, marginBottom: '16px' }}>
              Every client of this shop will see it, including clients who join later.
            </div>
          ) : loadingMembers ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: MUTED }}>
              <div className="spinner-sm mx-auto mb-2" />
              Loading members…
            </div>
          ) : membersError ? (
            <div style={{ fontSize: '0.8125rem', color: 'var(--error-text)', marginBottom: '12px' }}>{membersError}</div>
          ) : members.length === 0 ? (
            <div style={{ fontSize: '0.8125rem', color: MUTED, marginBottom: '12px' }}>
              No clients to share with yet. Invite a client first, or share with all clients.
            </div>
          ) : (
            <div style={{ border: `1px solid ${BORDER}`, borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
              {members.map((m, i) => {
                const selected = selectedUids.has(m.firebaseUid);
                return (
                  <label
                    key={m.firebaseUid}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', cursor: 'pointer',
                      borderTop: i === 0 ? 'none' : `1px solid ${BORDER}`,
                      backgroundColor: selected ? 'var(--bg-accent)' : 'var(--bg-card)',
                    }}
                  >
                    <input type="checkbox" checked={selected} onChange={() => toggleMember(m.firebaseUid)} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.875rem', color: TEXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {memberName(m)}
                      </div>
                      {m.email && m.displayName && (
                        <div style={{ fontSize: '0.75rem', color: MUTED, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.email}</div>
                      )}
                    </div>
                    <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: PRIMARY, backgroundColor: 'var(--bg-accent)', borderRadius: '10px', padding: '2px 8px' }}>
                      {(m.roles[0] && roleLabel(m.roles[0])) || 'Member'}
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          {/* Duration */}
          <div style={{ marginBottom: '12px' }}>
            <label htmlFor="share-duration" style={{ fontSize: '0.8125rem', fontWeight: 600, color: TEXT, display: 'block', marginBottom: '4px' }}>
              Share for
            </label>
            <select
              id="share-duration"
              value={duration}
              onChange={(e) => setDuration(e.target.value as ShareDurationId)}
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: `1px solid ${BORDER}`, borderRadius: '6px', fontSize: '0.875rem' }}
            >
              {SHARE_DURATIONS.map(d => (
                <option key={d.id} value={d.id}>{d.label}</option>
              ))}
            </select>
            <div style={{ fontSize: '0.75rem', color: MUTED, marginTop: '4px' }}>
              Sharing this collection again replaces the current share.
            </div>
          </div>

          {/* Title + reason */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '0.8125rem', fontWeight: 600, color: TEXT, display: 'block', marginBottom: '4px' }}>
              Title <span style={{ color: 'var(--error-text)' }}>*</span>
            </label>
            <input
              type="text"
              value={title}
              maxLength={TITLE_MAX}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Picked for you"
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: `1px solid ${BORDER}`, borderRadius: '6px', fontSize: '0.875rem' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '0.8125rem', fontWeight: 600, color: TEXT, display: 'block', marginBottom: '4px' }}>
              Message <span style={{ color: MUTED, fontWeight: 400 }}>(optional)</span>
            </label>
            <textarea
              value={reason}
              maxLength={REASON_MAX}
              onChange={(e) => setReason(e.target.value)}
              placeholder="A note shown under the cube, e.g. “A few things I thought you’d like.”"
              rows={2}
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: `1px solid ${BORDER}`, borderRadius: '6px', fontSize: '0.875rem', resize: 'vertical' }}
            />
          </div>

          {error && (
            <div style={{ marginTop: '12px', fontSize: '0.8125rem', color: 'var(--error-text)' }}>{error}</div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '20px' }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setConfirmUnshare(true)}
            disabled={submitting}
            style={{ color: 'var(--error-text)', marginRight: 'auto' }}
            title="Remove this collection's cube from every recipient's feed"
          >
            Stop sharing
          </button>
          <button className="btn btn-ghost" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="btn btn-primary" onClick={handleShare} disabled={!canShare}>
            {submitting ? 'Sharing…' : 'Share'}
          </button>
        </div>
      </div>
    </div>

    {/* Rendered as a sibling, not inside the overlay above, so clicks in the confirmation never
        bubble to this dialog's backdrop (which would close it). */}
    {confirmUnshare && (
      <ConfirmModal
        title="Stop Sharing"
        message={`Stop sharing "${collectionName}"? It will be removed from every recipient's feed.`}
        confirmLabel="Stop Sharing"
        destructive
        onConfirm={handleUnshare}
        onCancel={() => setConfirmUnshare(false)}
      />
    )}
    </>
  );
};

/** Pull a readable message out of a thrown request error, including C1's validationErrors (400). */
function parseError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  const jsonStart = raw.indexOf('{');
  if (jsonStart >= 0) {
    try {
      const body = JSON.parse(raw.slice(jsonStart));
      if (Array.isArray(body.validationErrors) && body.validationErrors.length) {
        return body.validationErrors.join(' ');
      }
      if (body.message) return body.message;
    } catch {
      /* fall through */
    }
  }
  return 'Could not share the collection. Please try again.';
}

export default ShareCollectionModal;
